import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { logger } from 'config/logger';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { useIndeedJobListQuery } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedJobListQuery';
import { useContextSelector } from 'use-context-selector';
import { getVacancyName } from 'utils/vacancyEnrichedUtils';
import { EnrichVacancyModalContext } from '../../../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalType } from '../../../EnrichVacancyModalContext/EnrichVacancyModalContext.types';

export const useVacancyTemplateList = (search: string) => {
  const [searchDebounced, setSearchDebounced] = useState(search);
  const modalType = useContextSelector(EnrichVacancyModalContext, (s) => s.modalType);

  useDebounce(() => setSearchDebounced(search), 230, [search]);

  const { data: vacancyList, isFetching: isFetchingVacancyList } = useEnrichedVacanciesQuery({
    search: searchDebounced,
    indeedOnly: modalType === EnrichVacancyModalType.Indeed,
  });
  const { data: indeedJobsList, isFetching: isFetchingIndeedJobsList } = useIndeedJobListQuery();

  const isLoadingVacancyList =
    isFetchingVacancyList ||
    search !== searchDebounced ||
    (modalType === EnrichVacancyModalType.Indeed && isFetchingIndeedJobsList);

  const filteredVacancyList = useMemo<Vacancy[]>(() => {
    if (modalType === EnrichVacancyModalType.Indeed) {
      const indeedVacancyList: Vacancy[] = [];
      const indeedVacancyListKeys = vacancyList
        .filter((vacancy) => vacancy.is_improved && vacancy.type === 'INDEED')
        .map((vacancy) => vacancy.job_indeed_id);

      indeedJobsList.forEach((job) => {
        if (job.key) {
          if (!indeedVacancyListKeys.includes(job.key)) {
            indeedVacancyList.push({
              name: job.name,
              value: job.key,
              job_indeed_id: job.key,
              origin_job_title: job.name,
              type: 'INDEED',
              originalIndeedJob: job,
            });
          }
        } else {
          logger.error(`Indeed job key is not defined: ${JSON.stringify(job)}`);
        }
      });

      return indeedVacancyList.filter((indeedJob) =>
        indeedJob.name.toLowerCase().includes(searchDebounced.toLowerCase()),
      );
    }

    if (modalType === EnrichVacancyModalType.Ats) {
      return vacancyList
        .filter((vacancy) => vacancy.job_title?.toLowerCase().includes(searchDebounced.toLowerCase()) && !vacancy.db_id)
        .map((vacancy) => ({ label: getVacancyName(vacancy), ...vacancy }));
    }

    throw Error('Invalid modal type');
  }, [vacancyList, searchDebounced, modalType, indeedJobsList]);

  return { list: filteredVacancyList, isLoading: isLoadingVacancyList };
};
